import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ImagePlus, Loader2, Download, Copy, Check, X, Share2, Palette, Sparkles, Monitor, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function GeneratedImage({ url, base64, prompt, onClose, scooterId, onSavedToGallery }) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const imgSrc = url || (base64 ? `data:image/png;base64,${base64}` : null);

  const handleDownload = () => {
    if (!imgSrc) return;
    const a = document.createElement("a");
    a.href = imgSrc;
    a.download = `scutersh_ai_${Date.now()}.png`;
    a.click();
    toast.success("Imagine descarcata!");
  };

  const handleCopyUrl = () => {
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("URL copiat!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveToGallery = async () => {
    if (!url || !scooterId || saved) return;
    setSaving(true);
    try {
      await api.post(`/scooters/${scooterId}/add-photo-url`, { photo_url: url });
      setSaved(true);
      toast.success("Imagine salvata in galeria scuterului!");
      if (onSavedToGallery) onSavedToGallery(url);
    } catch (err) {
      console.error("Save to gallery error:", err);
      toast.error("Eroare la salvarea in galerie");
    } finally {
      setSaving(false);
    }
  };

  if (!imgSrc) return null;

  return (
    <div className="bg-secondary/50 rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border flex-wrap gap-1">
        <span className="text-xs font-bold text-primary flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> IMAGINE AI GENERATA
        </span>
        <div className="flex gap-1 flex-wrap">
          {url && scooterId && (
            <button onClick={handleSaveToGallery} disabled={saving || saved}
              className={cn("text-xs px-2 py-1 rounded transition-colors flex items-center gap-1",
                saved ? "bg-green-500/20 text-green-400" : "bg-primary/20 text-primary hover:bg-primary/30")}
              data-testid="save-to-gallery-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : <FolderPlus className="w-3 h-3" />}
              {saved ? "Salvat!" : saving ? "..." : "Salveaza in galerie"}
            </button>
          )}
          <button onClick={handleDownload} className="text-xs px-2 py-1 rounded bg-secondary hover:bg-primary/20 transition-colors flex items-center gap-1">
            <Download className="w-3 h-3" /> Descarca
          </button>
          {url && (
            <button onClick={handleCopyUrl} className="text-xs px-2 py-1 rounded bg-secondary hover:bg-primary/20 transition-colors flex items-center gap-1">
              {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
              URL
            </button>
          )}
          <button onClick={onClose} className="text-xs px-1.5 py-1 rounded bg-secondary hover:bg-red-500/20 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      <img src={imgSrc} alt="AI Generated" className="w-full max-h-[400px] object-contain bg-black/50" />
      {prompt && <p className="px-3 py-2 text-[10px] text-muted-foreground italic border-t border-border truncate">Prompt: {prompt}</p>}
    </div>
  );
}

// ===== Background Replacement =====
export function AIBackgroundButton({ scooterId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [bgStyle, setBgStyle] = useState("showroom");

  const generate = async () => {
    setLoading(true); setResult(null);
    try {
      const resp = await api.post(`/ai/image/generate`, {
        scooter_id: scooterId,
        image_type: "background",
        background_style: bgStyle,
      });
      setResult(resp.data);
      toast.success("Imagine generata cu succes!");
    } catch (err) {
      console.error("AI image error:", err);
      toast.error(err.response?.data?.detail || "Eroare la generarea imaginii");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="ai-background-tool">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Palette className="w-4 h-4 mr-1" />}
          {loading ? "Se genereaza (~30s)..." : "Fond AI profesional"}
        </Button>
        <Select value={bgStyle} onValueChange={setBgStyle}>
          <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="showroom">Showroom</SelectItem>
            <SelectItem value="white">Fond alb</SelectItem>
            <SelectItem value="outdoor">Exterior</SelectItem>
            <SelectItem value="studio">Studio premium</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && <GeneratedImage url={result.url} base64={result.base64} prompt={result.prompt} onClose={() => setResult(null)} scooterId={scooterId} />}
    </div>
  );
}

// ===== Social Media Banner =====
export function AISocialBannerButton({ scooterId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [platform, setPlatform] = useState("facebook");

  const generate = async () => {
    setLoading(true); setResult(null);
    try {
      const resp = await api.post(`/ai/image/generate`, {
        scooter_id: scooterId,
        image_type: "banner",
        platform,
      });
      setResult(resp.data);
      toast.success("Banniere generata!");
    } catch (err) {
      console.error("AI banner error:", err);
      toast.error(err.response?.data?.detail || "Eroare la generarea bannierului");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="ai-banner-tool">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Monitor className="w-4 h-4 mr-1" />}
          {loading ? "Se genereaza (~30s)..." : "Banniere social media"}
        </Button>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-[120px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="olx">OLX</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {result && <GeneratedImage url={result.url} base64={result.base64} prompt={result.prompt} onClose={() => setResult(null)} scooterId={scooterId} />}
    </div>
  );
}

// ===== Promo Visual =====
export function AIPromoVisualButton({ scooterId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true); setResult(null);
    try {
      const resp = await api.post(`/ai/image/generate`, {
        scooter_id: scooterId,
        image_type: "promo",
      });
      setResult(resp.data);
      toast.success("Visual promo generat!");
    } catch (err) {
      console.error("AI promo error:", err);
      toast.error(err.response?.data?.detail || "Eroare la generarea vizualului");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="ai-promo-tool">
      <Button size="sm" onClick={generate} disabled={loading} variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
        {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
        {loading ? "Se genereaza (~30s)..." : "Visual promotional"}
      </Button>
      {result && <GeneratedImage url={result.url} base64={result.base64} prompt={result.prompt} onClose={() => setResult(null)} scooterId={scooterId} />}
    </div>
  );
}
